/**
 * @file Fabrique procedurale du chat kawaii (geometries partagees + ToonKit) : produit un CatRig complet (os, ancrage d'arme, chapeau/tenue du skin).
 */
import { Euler, Object3D, Quaternion, Vector3 } from 'three';
import type { BufferGeometry } from 'three';
import type { HatId, SkinDef } from '@config/types';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';
import { CAT_LAYOUT } from '../rig/CatRig';
import type { CatBones, CatRig } from '../rig/CatRig';
import { MergeBuilder, mixHex } from './MergeBuilder';

/** Couleurs fixes du visage / de l'anatomie. */
const CREAM = '#fff6ea';
const WHITE = '#ffffff';
const PINK_INNER = '#ffc1cc';
const NOSE = '#ff8fa8';
const EYE = '#2b2017';
const GOLD = '#f4c64e';

/** Positions des os (repere du parent). Hauteur totale du chat ~1.2 (tete incluse). */
const HIPS_Y = 0.26;
const TORSO_Y = 0.22;
const UPPER_Y = 0.34;
const HEAD_Y = 0.24;
const HEAD_Z = 0.02;
const SHOULDER = CAT_LAYOUT.shoulder;
/** Ancrage d'arme dans le repere de `upper` : entre les pattes avant. */
const MOUNT = CAT_LAYOUT.mount;
/** Position des "mains" par rapport a l'ancrage (elles suivent le recul de l'arme). */
const HAND_X = CAT_LAYOUT.handX;
const HAND_Y = CAT_LAYOUT.handY;

type BoneKey = 'torso' | 'head' | 'earL' | 'earR' | 'tail0' | 'tail1' | 'tail2' | 'armL' | 'armR' | 'legBL' | 'legBR' | 'hat';

interface BoneGeo {
  fill: BufferGeometry;
  hull: BufferGeometry | null;
}

/** Une piece du chat : geometrie (repere de l'os) + os qui la porte. Rendue par PartBatch avec la matrice monde de l'os. */
export interface CatPart {
  /** Cle d'enregistrement dans le PartBatch (unique par skin et par os). */
  key: string;
  bone: Object3D;
  fill: BufferGeometry;
  hull: BufferGeometry | null;
}

const HAT_COLORS: Record<HatId, string> = {
  none: WHITE,
  maid: WHITE,
  sailor: WHITE,
  chef: WHITE,
  nurse: WHITE,
  police: '#35457a',
  detective: '#a07c50',
  witch: '#4e3c86',
  tiara: GOLD,
  cowboy: '#8c5a35',
  captain: WHITE,
  wizard: '#6a4fb8',
  helmet: '#b4bccb',
  pirate: '#2b2530',
  bunny: '#f4ece6',
  beanie: '#58a9d8',
  headphones: '#2f2f3a',
  crown: GOLD,
};

/**
 * Proportions (chibi, unites monde, hauteur ~1.2) :
 *  - torso : capsule r 0.27 legerement aplatie en Z, couleur skin.fur ; ventre creme devant (si pas de haut) ; rayures (skin.stripes).
 *  - head : sphere r 0.42 (aplatie) ; museau blanc, yeux noirs + reflet, nez rose, joues roses ; oreilles (cones + interieur rose).
 *  - queue : 3 segments capsule, pivots en chaine depuis l'arriere du bassin, courbee vers le haut.
 *  - pattes arriere : capsules sous le bassin ; pattes avant (armL/armR) dirigees vers l'avant, "mains" blanches portees par weaponMount.
 *  - tenue (skin.outfit) et chapeau (skin.hat) : geometries simples teintees (outfitColor).
 *  Chaque os porte UNE piece a couleurs de sommets (+ sa coque de contour) : ~14 pieces par chat, rendues par un PartBatch
 *  (BatchedMesh : 2 draw calls pour tous les chats) a partir de la matrice monde des os (voir partsOf).
 *  Geometries partagees (GeometryLibrary) et mises en cache par skin.
 */
export class CatFactory {
  private readonly skinGeos = new Map<string, Map<BoneKey, BoneGeo>>();
  private handGeo: BoneGeo | null = null;
  private readonly parts = new WeakMap<CatRig, readonly CatPart[]>();

  constructor(
    readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {}

  /** Construit le rig decrit ci-dessus pour `skin` (sans arme : weaponMount vide, muzzle = null). */
  create(skin: SkinDef): CatRig {
    const geos = this.geometriesFor(skin);
    const mk = (name: string, parent: Object3D | null, x = 0, y = 0, z = 0): Object3D => {
      const o = new Object3D();
      o.name = name;
      o.position.set(x, y, z);
      parent?.add(o);
      return o;
    };
    const parts: CatPart[] = [];
    const attach = (bone: Object3D, key: BoneKey): void => {
      const g = geos.get(key);
      if (g) parts.push({ key: `cat:${skin.id}:${key}`, bone, fill: g.fill, hull: g.hull });
    };

    const root = mk('cat', null);
    const hips = mk('hips', root, 0, HIPS_Y, 0);
    const torso = mk('torso', hips, 0, TORSO_Y, 0);
    const legBL = mk('legBL', hips, SHOULDER.x * 0.52, 0, -0.02);
    const legBR = mk('legBR', hips, -SHOULDER.x * 0.52, 0, -0.02);
    const tailBase = mk('tail0', hips, 0, 0.1, -0.22);
    const tailMid = mk('tail1', tailBase, 0, 0.24, 0);
    const tailTip = mk('tail2', tailMid, 0, 0.24, 0);
    const upper = mk('upper', hips, 0, UPPER_Y, 0);
    const head = mk('head', upper, 0, HEAD_Y, HEAD_Z);
    const earL = mk('earL', head, 0.24, 0.3, 0);
    const earR = mk('earR', head, -0.24, 0.3, 0);
    const hatMount = mk('hatMount', head, 0, 0.34, 0);
    const armL = mk('armL', upper, SHOULDER.x, SHOULDER.y, SHOULDER.z);
    const armR = mk('armR', upper, -SHOULDER.x, SHOULDER.y, SHOULDER.z);
    const weaponMount = mk('weaponMount', upper, MOUNT.x, MOUNT.y, MOUNT.z);
    const handL = mk('handL', upper, MOUNT.x + HAND_X, MOUNT.y + HAND_Y, MOUNT.z);
    const handR = mk('handR', upper, MOUNT.x - HAND_X, MOUNT.y + HAND_Y, MOUNT.z);

    // ordre d'Euler lacet -> inclinaison locale : buste/racine (visee + recul), queue (balayage lateral autour de la verticale)
    for (const o of [root, upper, head, tailBase, tailMid, tailTip]) o.rotation.order = 'YXZ';
    // poses de repos : oreilles ecartees, queue recourbee vers le haut
    earL.rotation.z = -0.31;
    earR.rotation.z = 0.31;
    tailBase.rotation.x = -1.0;
    tailMid.rotation.x = 0.55;
    tailTip.rotation.x = 0.6;

    attach(torso, 'torso');
    attach(head, 'head');
    attach(earL, 'earL');
    attach(earR, 'earR');
    attach(tailBase, 'tail0');
    attach(tailMid, 'tail1');
    attach(tailTip, 'tail2');
    attach(armL, 'armL');
    attach(armR, 'armR');
    attach(legBL, 'legBL');
    attach(legBR, 'legBR');
    attach(hatMount, 'hat');
    const hand = this.hand();
    for (const bone of [handL, handR]) parts.push({ key: 'cat:hand', bone, fill: hand.fill, hull: hand.hull });

    const bones: CatBones = {
      root,
      hips,
      torso,
      upper,
      head,
      earL,
      earR,
      tail: [tailBase, tailMid, tailTip],
      armL,
      armR,
      legBL,
      legBR,
      weaponMount,
      hatMount,
      handL,
      handR,
    };
    const rest: CatRig['rest'] = new Map();
    for (const b of [hips, torso, upper, head, earL, earR, tailBase, tailMid, tailTip, armL, armR, legBL, legBR, weaponMount, hatMount, handL, handR]) {
      rest.set(b, { rx: b.rotation.x, ry: b.rotation.y, rz: b.rotation.z, px: b.position.x, py: b.position.y, pz: b.position.z });
    }
    const rig: CatRig = { bones, muzzle: null, weaponDefId: null, skinId: skin.id, rest };
    this.parts.set(rig, parts);
    return rig;
  }

  /** Pieces (geometrie + os) d'un rig cree par `create`, pour le rendu par lot. */
  partsOf(rig: CatRig): readonly CatPart[] {
    return this.parts.get(rig) ?? [];
  }

  /**
   * Libere un rig : retire de la scene ; ne dispose NI geometries NI materiaux (partages, geres par ResourceTracker).
   * Les rigs sont mis en pool par EntityViews (reutilises si meme skin).
   */
  release(rig: CatRig): void {
    rig.bones.root.removeFromParent();
    rig.bones.weaponMount.clear();
    rig.muzzle = null;
    rig.weaponDefId = null;
  }

  // ------------------------------------------------------------------------------------------------------------------

  private store(key: string, b: MergeBuilder): BoneGeo | null {
    const fill = b.fill('main');
    if (!fill) return null;
    const hull = b.hull();
    return { fill: this.geo.get(`${key}:fill`, () => fill), hull: hull ? this.geo.get(`${key}:hull`, () => hull) : null };
  }

  /** « Main » blanche (une seule geometrie partagee par les deux os handL / handR, positionnes par CatAnimator). */
  private hand(): BoneGeo {
    if (this.handGeo) return this.handGeo;
    const b = new MergeBuilder();
    b.add(this.geo.sphere(0.1, 10, 8), CREAM);
    const out = this.store('cat:hand', b);
    if (!out) throw new Error('CatFactory: main vide');
    this.handGeo = out;
    return out;
  }

  private geometriesFor(skin: SkinDef): Map<BoneKey, BoneGeo> {
    const cached = this.skinGeos.get(skin.id);
    if (cached) return cached;
    const out = new Map<BoneKey, BoneGeo>();
    const prefix = `cat:${skin.id}`;
    const put = (key: BoneKey, b: MergeBuilder): void => {
      const stored = this.store(`${prefix}:${key}`, b);
      if (stored) out.set(key, stored);
    };
    const fur = skin.fur;
    const stripes = skin.stripes;
    const g = this.geo;
    const outfit = skin.outfit;
    const outfitColor = skin.outfitColor ?? WHITE;

    // ------------------------------------------------------------ torse + tenue
    {
      const b = new MergeBuilder();
      b.add(g.capsule(0.27, 0.12, 6, 14), fur, { sz: 0.92 });
      if (outfit === 'none' || outfit === 'scarf' || outfit === 'cape') {
        b.add(g.sphere(0.2, 12, 10), CREAM, { y: -0.04, z: 0.15, sx: 1, sy: 1.15, sz: 0.62, outline: false });
      }
      if (stripes) {
        for (const [dy, r] of [[-0.12, 0.268], [0, 0.278], [0.12, 0.268]] as const) {
          b.add(g.torus(r, 0.02), stripes, { y: dy, rx: Math.PI / 2, sz: 0.92, outline: false });
        }
      }
      this.addOutfit(b, outfit, outfitColor);
      put('torso', b);
    }

    // ------------------------------------------------------------ tete
    {
      const b = new MergeBuilder();
      b.add(g.sphere(1, 24, 18), fur, { sx: 0.42, sy: 0.38, sz: 0.4 });
      b.add(g.sphere(0.13, 12, 10), WHITE, { y: -0.09, z: 0.35, sy: 0.85, sz: 0.9 });
      b.add(g.sphere(0.035, 8, 6), NOSE, { y: -0.04, z: 0.455, outline: false });
      for (const sx of [-1, 1]) {
        b.add(g.sphere(0.06, 10, 8), EYE, { x: sx * 0.16, y: 0.03, z: 0.37, sy: 1.3, sz: 0.7, outline: false });
        b.add(g.sphere(0.022, 6, 5), WHITE, { x: sx * 0.145, y: 0.08, z: 0.41, outline: false });
        b.add(g.sphere(0.06, 8, 6), PINK_INNER, { x: sx * 0.27, y: -0.08, z: 0.3, sy: 0.6, sz: 0.5, outline: false });
      }
      b.add(g.box(0.012, 0.045, 0.012), EYE, { y: -0.085, z: 0.455, outline: false });
      if (stripes) {
        for (const sx of [-1, 0, 1]) b.add(g.box(0.04, 0.02, 0.15), stripes, { x: sx * 0.1, y: 0.33, z: 0.14, rx: 0.45, outline: false });
      }
      put('head', b);
    }

    // ------------------------------------------------------------ oreilles (cote +x = gauche du chat)
    for (const [key, sx] of [['earL', 1], ['earR', -1]] as const) {
      const b = new MergeBuilder();
      b.add(g.cone(0.14, 0.26, 6), fur, { y: 0.13, sz: 0.55 });
      b.add(g.cone(0.08, 0.18, 6), PINK_INNER, { x: sx * 0.0, y: 0.1, z: 0.04, sz: 0.4, outline: false });
      put(key, b);
    }

    // ------------------------------------------------------------ queue : 3 segments, bout creme
    for (const [i, key] of (['tail0', 'tail1', 'tail2'] as const).entries()) {
      const b = new MergeBuilder();
      const col = stripes && i === 1 ? stripes : fur;
      b.add(g.capsule(0.07 - i * 0.004, 0.14, 4, 10), col, { y: 0.13 });
      if (i === 2) b.add(g.sphere(0.066, 10, 8), CREAM, { y: 0.27 });
      put(key, b);
    }

    // ------------------------------------------------------------ pattes avant (bras) : capsule epaule -> main
    for (const [key, sx] of [['armL', 1], ['armR', -1]] as const) {
      const b = new MergeBuilder();
      const dir = new Vector3(sx * HAND_X + MOUNT.x - sx * SHOULDER.x, MOUNT.y + HAND_Y - SHOULDER.y, MOUNT.z - SHOULDER.z);
      const len = dir.length();
      dir.normalize();
      const q = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), dir);
      const e = new Euler().setFromQuaternion(q, 'XYZ');
      const r = 0.085;
      const c = dir.clone().multiplyScalar(len / 2);
      b.add(g.capsule(r, Math.max(0.02, len - 2 * r), 5, 10), fur, { x: c.x, y: c.y, z: c.z, rx: e.x, ry: e.y, rz: e.z });
      put(key, b);
    }

    // ------------------------------------------------------------ pattes arriere
    for (const key of ['legBL', 'legBR'] as const) {
      const b = new MergeBuilder();
      b.add(g.capsule(0.095, 0.1, 4, 10), fur, { y: -0.13 });
      b.add(g.sphere(0.1, 10, 8), CREAM, { y: -0.25, z: 0.04, sy: 0.55, sz: 1.3 });
      put(key, b);
    }

    // ------------------------------------------------------------ chapeau
    if (skin.hat !== 'none') {
      const b = new MergeBuilder();
      this.addHat(b, skin.hat, skin.outfitColor);
      put('hat', b);
    }

    this.skinGeos.set(skin.id, out);
    return out;
  }

  private addOutfit(b: MergeBuilder, outfit: SkinDef['outfit'], color: string): void {
    const g = this.geo;
    switch (outfit) {
      case 'none':
        break;
      case 'scarf':
        b.add(g.torus(0.29, 0.075), color, { y: 0.25, rx: Math.PI / 2, sz: 0.92 });
        b.add(g.box(0.1, 0.3, 0.06), color, { x: 0.18, y: 0.08, z: 0.27, rz: -0.2 });
        break;
      case 'shirt':
        b.add(g.capsule(0.285, 0.1, 6, 14), color, { y: 0.0, sz: 0.92 });
        b.add(g.torus(0.2, 0.03), mixHex(color, WHITE, 0.55), { y: 0.3, rx: Math.PI / 2, outline: false });
        break;
      case 'apron':
        b.add(g.box(0.36, 0.42, 0.04), color, { y: -0.04, z: 0.255, rx: -0.04 });
        b.add(g.box(0.22, 0.16, 0.04), color, { y: 0.22, z: 0.235 });
        b.add(g.box(0.04, 0.2, 0.04), color, { x: 0.13, y: 0.3, z: 0.2, rz: 0.2, outline: false });
        b.add(g.box(0.04, 0.2, 0.04), color, { x: -0.13, y: 0.3, z: 0.2, rz: -0.2, outline: false });
        break;
      case 'sailor':
        b.add(g.capsule(0.285, 0.1, 6, 14), color, { y: 0.0, sz: 0.92 });
        b.add(g.torus(0.23, 0.05), '#2f4f9a', { y: 0.27, rx: Math.PI / 2, sz: 0.92 });
        b.add(g.sphere(0.055, 8, 6), '#e5525f', { y: 0.2, z: 0.26, sx: 1.4, outline: false });
        break;
      case 'cape':
        b.add(g.box(0.52, 0.58, 0.05), color, { y: 0.0, z: -0.26, rx: 0.16 });
        b.add(g.torus(0.27, 0.05), color, { y: 0.27, rx: Math.PI / 2, sz: 0.92 });
        b.add(g.sphere(0.05, 8, 6), GOLD, { y: 0.24, z: 0.26, outline: false });
        break;
      case 'dress':
        b.add(g.cylinder(0.24, 0.42, 0.38, 16), color, { y: -0.12 });
        b.add(g.torus(0.25, 0.03), mixHex(color, WHITE, 0.5), { y: 0.06, rx: Math.PI / 2, outline: false });
        break;
      case 'armor':
        b.add(g.capsule(0.29, 0.06, 6, 14), color, { sz: 0.94 });
        b.add(g.torus(0.285, 0.04), mixHex(color, EYE, 0.45), { y: -0.1, rx: Math.PI / 2, sz: 0.94 });
        b.add(g.box(0.08, 0.07, 0.04), GOLD, { y: -0.1, z: 0.27 });
        for (const sx of [-1, 1]) b.add(g.sphere(0.12, 10, 8), color, { x: sx * 0.28, y: 0.26, sy: 0.7 });
        break;
    }
  }

  /** Chapeaux dans le repere de `hatMount` (origine = sommet de la tete, +Y haut). */
  private addHat(b: MergeBuilder, hat: HatId, outfitColor: string | undefined): void {
    const g = this.geo;
    const base = outfitColor && ['police', 'witch', 'wizard', 'beanie', 'captain'].includes(hat) ? outfitColor : HAT_COLORS[hat];
    const dark = mixHex(base, EYE, 0.5);
    switch (hat) {
      case 'none':
        break;
      case 'maid':
        b.add(g.torus(0.3, 0.045), WHITE, { y: -0.06, z: 0.04, rx: Math.PI / 2 - 0.35 });
        b.add(g.sphere(1, 12, 8), WHITE, { y: 0.0, z: 0.14, sx: 0.3, sy: 0.07, sz: 0.2 });
        b.add(g.sphere(0.06, 8, 6), '#5b4a42', { x: 0.14, y: 0.02, z: 0.2, outline: false });
        b.add(g.sphere(0.06, 8, 6), '#5b4a42', { x: -0.14, y: 0.02, z: 0.2, outline: false });
        break;
      case 'sailor':
        b.add(g.cylinder(0.25, 0.3, 0.16, 14), WHITE, { y: 0.06 });
        b.add(g.cylinder(0.305, 0.305, 0.05, 14), '#2f4f9a', { y: -0.01 });
        b.add(g.sphere(0.05, 8, 6), '#e5525f', { x: 0.2, y: 0.02, z: 0.22, outline: false });
        break;
      case 'chef':
        b.add(g.cylinder(0.24, 0.26, 0.1, 14), WHITE, { y: 0.0 });
        b.add(g.cylinder(0.24, 0.22, 0.22, 14), WHITE, { y: 0.14 });
        b.add(g.sphere(1, 14, 10), WHITE, { y: 0.34, s: 0.3, sy: 0.75 });
        break;
      case 'nurse':
        b.add(g.cylinder(0.2, 0.25, 0.13, 14), WHITE, { y: 0.04 });
        b.add(g.box(0.1, 0.035, 0.03), '#e24a4a', { y: 0.06, z: 0.225, rx: -0.2, outline: false });
        b.add(g.box(0.035, 0.1, 0.03), '#e24a4a', { y: 0.06, z: 0.225, rx: -0.2, outline: false });
        break;
      case 'police':
        b.add(g.cylinder(0.26, 0.31, 0.17, 14), base, { y: 0.06 });
        b.add(g.sphere(1, 10, 6), dark, { y: -0.0, z: 0.3, sx: 0.28, sy: 0.04, sz: 0.2 });
        b.add(g.sphere(0.045, 8, 6), GOLD, { y: 0.07, z: 0.31, outline: false });
        break;
      case 'detective':
        b.add(g.cylinder(0.44, 0.44, 0.03, 18), base, { y: -0.03 });
        b.add(g.cylinder(0.22, 0.28, 0.2, 14), base, { y: 0.08 });
        b.add(g.cylinder(0.285, 0.285, 0.05, 14), dark, { y: 0.0 });
        break;
      case 'witch':
        b.add(g.cylinder(0.42, 0.42, 0.04, 18), base, { y: -0.03 });
        b.add(g.cone(0.27, 0.62, 14), base, { y: 0.3, x: 0.02, rz: -0.12 });
        b.add(g.cylinder(0.275, 0.285, 0.07, 14), dark, { y: 0.03 });
        b.add(g.box(0.1, 0.09, 0.03), GOLD, { y: 0.03, z: 0.285, outline: false });
        break;
      case 'tiara':
        b.add(g.torus(0.2, 0.025), GOLD, { y: 0.0, z: 0.12, rx: Math.PI / 2 - 0.5 });
        for (const dx of [-0.1, 0, 0.1]) b.add(g.cone(0.035, 0.1, 5), GOLD, { x: dx, y: 0.1 - Math.abs(dx) * 0.4, z: 0.28 });
        b.add(g.sphere(0.04, 8, 6), '#ff6f91', { y: 0.03, z: 0.31, outline: false });
        break;
      case 'cowboy':
        b.add(g.cylinder(0.46, 0.46, 0.03, 18), base, { y: -0.03, sx: 1, sz: 0.9 });
        b.add(g.cylinder(0.22, 0.26, 0.22, 14), base, { y: 0.09 });
        b.add(g.cylinder(0.265, 0.265, 0.05, 14), '#d8493e', { y: 0.0 });
        break;
      case 'captain':
        b.add(g.cylinder(0.3, 0.27, 0.15, 14), WHITE, { y: 0.07 });
        b.add(g.cylinder(0.275, 0.275, 0.05, 14), base, { y: 0.0 });
        b.add(g.sphere(1, 10, 6), '#1c1c26', { y: -0.02, z: 0.3, sx: 0.28, sy: 0.04, sz: 0.2 });
        b.add(g.sphere(0.05, 8, 6), GOLD, { y: 0.08, z: 0.29, outline: false });
        break;
      case 'wizard':
        b.add(g.cylinder(0.36, 0.36, 0.04, 18), base, { y: -0.03 });
        b.add(g.cone(0.26, 0.72, 14), base, { y: 0.36, x: -0.03, rz: 0.1 });
        b.add(g.sphere(0.045, 8, 6), GOLD, { x: 0.06, y: 0.28, z: 0.22, outline: false });
        b.add(g.sphere(0.035, 8, 6), GOLD, { x: -0.08, y: 0.45, z: 0.14, outline: false });
        b.add(g.cylinder(0.265, 0.275, 0.06, 14), GOLD, { y: 0.02 });
        break;
      case 'helmet':
        b.add(g.sphere(1, 16, 12), mixHex(HAT_COLORS.helmet, WHITE, 0.3), { y: 0.03, sx: 0.4, sy: 0.25, sz: 0.4 });
        b.add(g.box(0.3, 0.035, 0.03), EYE, { y: 0.0, z: 0.385, outline: false });
        b.add(g.cone(0.06, 0.3, 6), '#e5525f', { y: 0.28, z: -0.05, rx: -0.4 });
        break;
      case 'pirate':
        b.add(g.cylinder(0.4, 0.4, 0.045, 18), base, { y: -0.01, sz: 0.85 });
        b.add(g.cylinder(0.2, 0.3, 0.16, 14), base, { y: 0.09 });
        b.add(g.torus(0.4, 0.02), GOLD, { y: 0.0, rx: Math.PI / 2, sz: 0.85, outline: false });
        b.add(g.sphere(0.055, 8, 6), WHITE, { y: 0.1, z: 0.28, outline: false });
        b.add(g.sphere(0.015, 6, 4), EYE, { x: -0.02, y: 0.11, z: 0.33, outline: false });
        b.add(g.sphere(0.015, 6, 4), EYE, { x: 0.02, y: 0.11, z: 0.33, outline: false });
        break;
      case 'bunny':
        for (const sx of [-1, 1]) {
          b.add(g.capsule(0.09, 0.42, 5, 10), base, { x: sx * 0.13, y: 0.36, rz: -sx * 0.18, sz: 0.7 });
          b.add(g.capsule(0.05, 0.34, 4, 8), PINK_INNER, { x: sx * 0.13 + sx * 0.0, y: 0.36, z: 0.03, rz: -sx * 0.18, sz: 0.5, outline: false });
        }
        break;
      case 'beanie':
        b.add(g.sphere(1, 16, 12), base, { y: 0.0, sx: 0.37, sy: 0.3, sz: 0.37 });
        b.add(g.torus(0.35, 0.05), mixHex(base, WHITE, 0.6), { y: -0.07, rx: Math.PI / 2 });
        b.add(g.sphere(0.1, 10, 8), WHITE, { y: 0.3 });
        break;
      case 'headphones':
        b.add(g.torus(0.43, 0.035), base, { y: -0.3 });
        for (const sx of [-1, 1]) b.add(g.cylinder(0.12, 0.12, 0.1, 12), '#f2c43a', { x: sx * 0.44, y: -0.3, rz: Math.PI / 2 });
        break;
      case 'crown':
        b.add(g.cylinder(0.26, 0.22, 0.12, 14), GOLD, { y: 0.03 });
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2;
          b.add(g.cone(0.05, 0.15, 5), GOLD, { x: Math.sin(a) * 0.23, y: 0.15, z: Math.cos(a) * 0.23 });
        }
        b.add(g.sphere(0.04, 8, 6), '#e5525f', { y: 0.04, z: 0.25, outline: false });
        break;
    }
  }
}
